import React from "react";
import { kval, bookAmount, toCurrency, bookClass } from "../../casinoComponents/BetTableCard32/card32Utils";
import { abMain } from "./abHelpers";

const AB_IMG = `${import.meta.env.BASE_URL}img/andar-bahar-cards`;
const CARD_IMG = `${import.meta.env.BASE_URL}img/cards`;
const KEYS = Array.from({ length: 13 }, (_, i) => i + 1);

// v-casinofancybook: amount (0 stays "0", else toCurrency) + book-green / book-red
const bookText = (amt) => (amt ? toCurrency(amt) : amt);

/**
 * Andar Bahar (ab20) bet area, reference markup (inside .casino-detail):
 *   .ab-bg > .andar-cards-box.text-center + .bahar-cards-box.text-center
 *     > h5.w-100.text-center.text-playera|b ("Andar" + .casino-min-max R:min-max of main[0])
 *       + 13 × .casino-card-item > .card-image > img andar-bahar-cards/{ares|bres[i] or 1..13}.png + .casino-book
 * Card image: feed `ares` / `bres` (13 values) when complete, otherwise the 1..13 placeholder.
 */
export function Ab20BetTable({ gameType, feed, exposures = {}, onBetClick }) {
  const main = abMain(feed?.sub, gameType);
  const andarAll = feed?.ares ? String(feed.ares).split(",") : [];
  const baharAll = feed?.bres ? String(feed.bres).split(",") : [];
  const m0 = main[0] || {};

  const box = (side, all, offset) => (
    <div className={`${side}-cards-box text-center`}>
      <h5 className={`w-100 text-center text-player${side === "andar" ? "a" : "b"}`}>
        {`\r\n                                    ${side === "andar" ? "Andar" : "Bahar"}\r\n                                    `}
        <div className="casino-min-max">
          R:<span>{kval(m0.min)}</span>-<span>{kval(m0.max)}</span>
        </div>
      </h5>{" "}
      {KEYS.map((n, i) => {
        const m = main[i + offset] || {};
        const amt = bookAmount(exposures, m.nat);
        return (
          <div key={`${side}-${i}`} className="casino-card-item" onClick={() => onBetClick?.(m.nat, m.nat, null, m)}>
            <div className="card-image">
              <img src={`${AB_IMG}/${all.length === 13 ? all[i] : n}.png`} alt="" />
            </div>{" "}
            <div className={`casino-book ${bookClass(amt)}`}>{bookText(amt)}</div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="ab-bg">
      {box("andar", andarAll, 0)}{" "}
      {box("bahar", baharAll, 13)}
    </div>
  );
}

const SUITS = [
  ["spade", 19],
  ["heart", 21],
  ["club", 20],
  ["diamond", 22],
];
const CARD_FACE = { 1: "A", 11: "J", 12: "Q", 13: "K" };

/**
 * Andar Bahar 2 (abj) bet area, reference markup (inside .casino-detail, after the mobile card strip):
 *   .teen20casino-container > .teen20left (A: SA, 1st Bet, 2nd Bet) + .teen20center + .teen20right (B)
 *   .teen20casino-container > .teen20left.ab2oddeven (Odd / Even = main[23], main[24])
 *     + .teen20center + .teen20left.ab2cards (spade / heart / club / diamond = main[19], [21], [20], [22])
 *   .teen20casino-container.ab2allcards (joker title main[6].b + 13 cards main[6..18])
 *   .text-right.casino-min-max (R: main[0] min-max)
 * `.suspended` when gstatus is not OPEN; the book directive adds `.lock-top` to a suspended parent.
 */
export function AbjBetTable({ gameType, feed, exposures = {}, onBetClick }) {
  const main = abMain(feed?.sub, gameType);
  const at = (i) => main[i] || {};
  const susp = (i) => at(i).gstatus !== "OPEN";
  const amt = (i, name = at(i).nat) => bookAmount(exposures, name);
  const click = (i) => () => !susp(i) && onBetClick?.(at(i).b, at(i).nat, "back", at(i));

  // 1st / 2nd Bet are stored with their side ("1st Bet SA" / "2nd Bet SB"); the feed names
  // both sides' rows "1st Bet", so the book is read by the stored name.
  const sideBet = (i, cls, name) => (
    <div className={`${cls}${susp(i) ? " suspended lock-top" : ""}`} onClick={click(i)}>
      <div>{at(i).nat}</div> <div>{at(i).b}</div>{" "}
      <div className={`ab-book ${bookClass(amt(i, name))}`}>{bookText(amt(i, name))}</div>
    </div>
  );

  const side = (label, cls, base) => (
    <div className={cls}>
      <div className="ab2-title">{label}</div> {sideBet(base, "sa-sb-box")}{" "}
      {sideBet(base + 1, "ab2-bet", `${at(base + 1).nat} S${label}`)}{" "}
      {sideBet(base + 2, "ab2-bet", `${at(base + 2).nat} S${label}`)} <div className="ab2-title">{label}</div>
    </div>
  );

  const oddBox = (i) => (
    <div className="casino-bl-box">
      <div className={`back casino-bl-box-item${susp(i) ? " suspended" : ""}`} onClick={click(i)}>
        <span className="casino-box-odd">{at(i).b}</span>
      </div>{" "}
      <span className={`casino-book ${bookClass(amt(i))}`}>{bookText(amt(i))}</span>
    </div>
  );

  return (
    <>
      <div className="teen20casino-container">
        {side("A", "teen20left", 0)} <div className="teen20center" /> {side("B", "teen20right", 3)}
      </div>{" "}
      <div className="teen20casino-container">
        <div className="teen20left ab2oddeven">
          <div className="casino-box-row">
            <div className="casino-bl-box">
              <div className="casino-bl-box-item">
                <b>Odd</b>
              </div>
            </div>{" "}
            <div className="casino-bl-box">
              <div className="casino-bl-box-item">
                <b>Even</b>
              </div>
            </div>
          </div>{" "}
          <div className="casino-box-row">
            {oddBox(23)} {oddBox(24)}
          </div>
        </div>{" "}
        <div className="teen20center" />{" "}
        <div className="teen20left ab2cards">
          <div className="casino-box-row">
            {SUITS.map(([suit], k) => (
              <React.Fragment key={suit}>
                {k ? " " : null}
                <div className="casino-bl-box">
                  <div className="casino-bl-box-item casino-card-img">
                    <img src={`${CARD_IMG}/${suit}.png`} alt="" />
                  </div>
                </div>
              </React.Fragment>
            ))}
          </div>{" "}
          <div className="casino-box-row">
            {SUITS.map(([suit, i], k) => (
              <React.Fragment key={suit}>
                {k ? " " : null}
                {oddBox(i)}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>{" "}
      <div className="teen20casino-container ab2allcards">
        <div className="text-center w-100">
          <div className="casino-bl-box casino-cards-odds-title">
            <div className="casino-bl-box-item">
              <b>{at(6).b}</b>
            </div>
          </div>
        </div>{" "}
        <div className="casino-cards text-center mt-1">
          {KEYS.map((n) => (
            <div key={n} className="casino-card-item" onClick={click(n + 5)}>
              <div className={`card-image${susp(n + 5) ? " suspended" : ""}`}>
                <img src={`${CARD_IMG}/${CARD_FACE[n] || n}.png`} alt="" />
              </div>{" "}
              <div className={`casino-book ${bookClass(amt(n + 5))}`}>{bookText(amt(n + 5))}</div>
            </div>
          ))}
        </div>
      </div>{" "}
      <div className="text-right casino-min-max">
        R:<span>{kval(at(0).min)}</span>-<span>{kval(at(0).max)}</span>
      </div>
    </>
  );
}
